<template>
  <div id="cohort-distribution" ref="cohort"></div>
</template>

<script>
import _ from "lodash";
import { fetchCohort } from "@/utils/api";
import fieldDefinitions from "@/utils/fields";
import { mapGetters } from "vuex";

const ptColor = "#F2994A";
const textColor = "#00113D";
const violinColor = "#A0A0A066";

export default {
  name: "CohortPlots",
  mounted() {
    this.updateCohort()
  },
  watch: {
    patientData() {
      this.updateCohort();
      this.plotPatientData();
    },
    componentWidth() {
      this.renderCohortPlot();
    }
  },
  computed: {
    ...mapGetters(["patientData"]),
    componentWidth() {
      return (
        (this.$refs.cohort &&
          this.$refs.cohort.parentElement &&
          this.$refs.cohort.parentElement.clientWidth) ||
        800
      );
    }
  },
  data() {
    return {
      svg: null,
      cohortData: null,
      fieldsToPlot: _.pickBy(fieldDefinitions, i => i.validation)
    };
  },
  methods: {
    updateCohort() {
      if (!this.cohortData) {
        fetchCohort().then(({ cohort }) => {
          this.cohortData = cohort;
          this.renderCohortPlot();
        });
      }
    },
    // 2 functions needed for kernel density estimate
    kernelDensityEstimator(kernel, X) {
      return V => X.map(x => [x, this.$d3.mean(V, v => kernel(x - v))]);
    },

    kernelEpanechnikov(k) {
      return v => (Math.abs((v /= k)) <= 1 ? (0.75 * (1 - v * v)) / k : 0);
    },

    closest_density(num, arr) {
      let curr = arr[0][1];
      let diff = Math.abs(num - arr[0][0]);
      for (let val = 0; val < arr.length; val++) {
        let newdiff = Math.abs(num - arr[val][0]);
        if (newdiff < diff) {
          diff = newdiff;
          curr = arr[val][1];
        }
      }
      return curr;
    },

    renderCohortPlot() {
      // set the dimensions and margins of the graph
      const margin = { top: 40, right: 10, bottom: 40, left: 10 };
      const width = this.componentWidth - margin.left - margin.right;
      const height = 160 - margin.top - margin.bottom;
      const boxWidth = 70;

      // remove previous plots if calculate >1 times
      this.$d3.selectAll("#cohort-distribution svg").remove();

      // append the svg object to the body of the page
      this.svg = this.$d3
        .select("#cohort-distribution")
        .append("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      const data = this.cohortData;
      const confidenceIntervals = {};
      const lowCI = 0.025;
      const highCI = 0.975;

      // Extract the list of dimensions we want to keep in the plot
      const dimensions = this.$d3
        .keys(data[0])
        .filter(d => Object.keys(this.fieldsToPlot).includes(d));

      // For each dimension build a linear scale and store all in y object
      const y = {};
      const vx = {};
      const ys = dimensions.map(dimension => {
        const vals = data.map(d => d[dimension]).sort(this.$d3.ascending);
        const maxValInCohort = Math.max.apply(Math, vals);

        const q1 = this.$d3.quantile(vals, 0.25);
        const median = this.$d3.quantile(vals, 0.5);
        const q3 = this.$d3.quantile(vals, 0.75);
        const iq = q3 - q1;
        const min = q1 - 1.5 * iq > 0 ? q1 - 1.5 * iq : 0;
        const max =
          q3 + 1.5 * iq < maxValInCohort ? q3 + 1.5 * iq : maxValInCohort;

        confidenceIntervals[dimension] = [
          this.$d3.quantile(vals, lowCI),
          this.$d3.quantile(vals, highCI)
        ];

        y[dimension] = this.$d3
          .scaleLinear()
          .domain([min, max])
          .range([height, 0]);

        // Features of density estimate
        const kde = this.kernelDensityEstimator(
          this.kernelEpanechnikov((max - min) / 20),
          y[dimension].ticks(40)
        );
        const density = kde(vals);
        const q1den = this.closest_density(q1, density);
        const medianden = this.closest_density(median, density);
        const q3den = this.closest_density(q3, density);
        const maxNum = this.$d3.max(density.map(a => a[1]));

        vx[dimension] = this.$d3
          .scaleLinear()
          .range([0, boxWidth])
          .domain([-maxNum, maxNum]);

        return {
          attr: dimension,
          q1,
          q1den,
          median,
          medianden,
          q3,
          q3den,
          min,
          max,
          density
        };
      });

      this.$store.commit("setConfidenceIntervals", confidenceIntervals);

      // Build the X scale -> it finds the best position for each Y axis
      const x = this.$d3
        .scalePoint()
        .range([0, width])
        .padding(0.5)
        .domain(dimensions);

      // Show for the q1
      this.svg
        .selectAll("q1-lines")
        .data(ys)
        .enter()
        .append("line")
        .attr("class", "q1-lines")
        .attr("transform", d => `translate(${x(d.attr) - boxWidth / 2} ,0)`)
        .attr("x1", d => vx[d.attr](-d.q1den))
        .attr("x2", d => vx[d.attr](d.q1den))
        .attr("y1", d => y[d.attr](d.q1))
        .attr("y2", d => y[d.attr](d.q1))
        .attr("stroke", textColor)
        .attr("stroke-dasharray", 4)
        .style("fill", "white");

      this.svg
        .selectAll("median-lines")
        .data(ys)
        .enter()
        .append("line")
        .attr("class", "median-lines")
        .attr("transform", d => `translate(${x(d.attr) - boxWidth / 2} ,0)`)
        .attr("x1", d => vx[d.attr](-d.medianden))
        .attr("x2", d => vx[d.attr](d.medianden))
        .attr("y1", d => y[d.attr](d.median))
        .attr("y2", d => y[d.attr](d.median))
        .attr("stroke", textColor);

      // Show the q3
      this.svg
        .selectAll("q3-lines")
        .data(ys)
        .enter()
        .append("line")
        .attr("class", "q3-lines")
        .attr("transform", d => `translate(${x(d.attr) - boxWidth / 2} ,0)`) // Translation on the right to be at the group position
        .attr("x1", d => vx[d.attr](-d.q3den))
        .attr("x2", d => vx[d.attr](d.q3den))
        .attr("y1", d => y[d.attr](d.q3))
        .attr("y2", d => y[d.attr](d.q3))
        .attr("stroke", textColor)
        .attr("stroke-dasharray", 4);

      // Violin
      const violinPath = this.$d3
        .area()
        .x0(d => vx[d[2]](-d[1]))
        .x1(d => vx[d[2]](d[1]))
        .y(d => y[d[2]](d[0]))
        .curve(this.$d3.curveBasis);

      this.svg
        .selectAll("violin")
        .data(ys)
        .enter() // So now we are working group per group
        .append("g")
        .attr("class", "violin")
        .attr("transform", d => `translate(${x(d.attr) - boxWidth / 2} ,0)`) // Translation on the right to be at the group position
        .append("path")
        .datum(d => d.density.map(x => x.concat([d.attr]))) // So now we are working density per density
        .style("stroke", "none")
        .style("fill", violinColor)
        .attr("d", violinPath);

      // Draw the axis:
      this.svg
        .selectAll("violin-y-axis")
        .data(dimensions)
        .enter()
        .append("g")
        .attr("class", "violin-y-axis")
        .attr("transform", d => `translate(${x(d) - boxWidth / 2}, 0)`)
        // Build the axis with the call function
        .each((d, i, dim) => {
          this.$d3.select(dim[i]).call(
            this.$d3
              .axisLeft()
              .scale(y[d])
              .ticks(5)
          );
        })
        // Add axis title
        .append("text")
        .style("text-anchor", "middle")
        .attr("y", -9)
        .attr("x", boxWidth / 2)
        .text(d => this.fieldsToPlot[d].label)
        .style("fill", textColor)
        .style("font-size", 16);

      // Draw the axis:
      this.svg
        .selectAll("violin-ci-text")
        .data(dimensions)
        .enter()
        .append("g")
        .attr("class", "violin-ci-text")
        .attr("transform", d => `translate(${x(d) - boxWidth / 2}, 0)`)
        // Add axis title
        .append("text")
        .style("text-anchor", "middle")
        .attr("y", height + 20)
        .attr("x", boxWidth / 2)
        .html(d => {
          const [lowCI, highCI] = confidenceIntervals[d];
          const units = (fieldDefinitions[d].units || "")
            .replace("<sup>", '<tspan baseline-shift="super" class="sup">')
            .replace("</sup>", "</tspan>");
          return `95% [${Math.round(lowCI, 1)}, ${Math.round(
            highCI,
            1
          )}] ${units}`;
        })
        .style("fill", textColor)
        .style("font-size", 10);

      // Store in component data
      this.x = x;
      this.y = y;
    },

    plotPatientData() {
      const patientData = Object.entries(this.patientData)
        .filter(([key]) => Object.keys(this.fieldsToPlot).includes(key))
        .map(([key, value]) => ({ key: key, val: value }));

      this.svg.selectAll("#cohort-distribution .dot").remove();
      this.svg.selectAll("#cohort-distribution .dot-text").remove();

      // Appends a Circle for the patient datapoint
      this.svg
        .selectAll(".dot")
        .data(patientData)
        .enter()
        .append("circle")
        .attr("class", "dot")
        .attr("cx", d => this.x(d.key))
        .attr("cy", d => this.y[d.key](d.val))
        .attr("r", 6)
        .attr("fill", ptColor)
        .attr("stroke", "white")
        .attr("stroke-width", 2);

      this.svg
        .selectAll(".dot-text")
        .data(patientData)
        .enter()
        .append("text")
        .attr("class", "dot-text")
        .attr("x", d => this.x(d.key) + 12)
        .attr("y", d => this.y[d.key](d.val) + 5)
        .text(d => d.val)
        .style("fill", textColor);
    }
  }
};
</script>

<style lang="scss">
.violin-y-axis {
  path {
    stroke: #ccc;
  }
  .tick line {
    stroke: #ccc;
  }
  text {
    font-family: DM Sans;
  }
}
#cohort-distribution {
  overflow: auto;
  align-items: center;
  display: flex;
  margin: auto;

  .violin-ci-text .sup {
    font-size: 7px;
  }
}
</style>
